import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, can } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import TransactionDialog from "@/components/TransactionDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatUSD, formatMoney } from "@/components/KPICard";
import { formatDMY } from "@/lib/dates";
import { TRANSACTIONS } from "@/constants/testIds";

export default function Transactions() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [kind, setKind] = useState("all");
  const [country, setCountry] = useState("all");
  const [propId, setPropId] = useState("all");

  const load = async () => {
    const params = new URLSearchParams();
    if (kind !== "all") params.append("kind", kind);
    if (country !== "all") params.append("country", country);
    if (propId !== "all") params.append("property_id", propId);
    const [tx, pr] = await Promise.all([
      api.get(`/transactions?${params.toString()}`),
      api.get(`/properties`),
    ]);
    setItems(tx.data);
    setProperties(pr.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, country, propId]);

  const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);

  const totals = useMemo(() => {
    let inc = 0, exp = 0;
    for (const t of items) { if (t.kind === "income") inc += t.amount_usd; else exp += t.amount_usd; }
    return { inc, exp, net: inc - exp };
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("nav.transactions")}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {items.length} movimientos · Ingresos {formatUSD(totals.inc)} · Egresos {formatUSD(totals.exp)} · Neto {formatUSD(totals.net)}
          </div>
        </div>
        {can(user, "manageTransactions") && (
          <Button className="rounded-full" onClick={() => setDialogOpen(true)} data-testid={TRANSACTIONS.addBtn}>
            <Plus className="w-4 h-4 mr-1" /> {t("tx.add")}
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[140px]">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Tipo</div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid={TRANSACTIONS.filterKind}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="income">Ingresos</SelectItem>
              <SelectItem value="expense">Gastos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">País</div>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid={TRANSACTIONS.filterCountry}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="AR">Argentina</SelectItem>
              <SelectItem value="US">EE.UU.</SelectItem>
              <SelectItem value="MX">México</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px] flex-1">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Propiedad</div>
          <Select value={propId} onValueChange={setPropId}>
            <SelectTrigger data-testid={TRANSACTIONS.filterProperty}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-xl border border-border/60 overflow-hidden" data-testid={TRANSACTIONS.table}>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="px-3 py-2.5 font-medium">Fecha</th>
              <th className="px-3 py-2.5 font-medium">Propiedad</th>
              <th className="px-3 py-2.5 font-medium">Tipo</th>
              <th className="px-3 py-2.5 font-medium">Categoría</th>
              <th className="px-3 py-2.5 font-medium">Descripción</th>
              <th className="px-3 py-2.5 font-medium text-right">Monto</th>
              <th className="px-3 py-2.5 font-medium text-right">USD</th>
            </tr>
          </thead>
          <tbody>
            {items.map((tx) => {
              const p = propMap[tx.property_id] || {};
              return (
                <tr key={tx.id} className="border-t border-border/50 hover:bg-secondary/40 transition-colors">
                  <td className="px-3 py-2.5 num text-xs">{formatDMY(tx.date)}</td>
                  <td className="px-3 py-2.5">
                    <div className="text-sm">{p.name || "-"}</div>
                    <div className="text-[11px] text-muted-foreground">{p.country}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={tx.kind === "income" ? "text-emerald-500" : "text-rose-500"}>
                      {tx.kind === "income" ? "Ingreso" : "Gasto"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 capitalize">{tx.category}</td>
                  <td className="px-3 py-2.5 text-muted-foreground max-w-xs truncate">{tx.description}</td>
                  <td className="px-3 py-2.5 text-right num">{formatMoney(tx.amount, tx.currency)}</td>
                  <td className="px-3 py-2.5 text-right num font-semibold">{formatUSD(tx.amount_usd)}</td>
                </tr>
              );
            })}
            {!items.length && (
              <tr><td colSpan={7} className="px-3 py-12 text-center text-muted-foreground">Sin movimientos.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <TransactionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        properties={properties}
        onSaved={load}
      />
    </div>
  );
}
